import React, { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboard, faFileExcel } from '@fortawesome/free-regular-svg-icons';
import isNil from 'lodash/isNil';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import {
  fullStockReportFiltersSelector,
  fullStockReportGlobalFiltersSelector, fullStockReportTreeSelector,
} from '../../../../../store/selectors/reportsSelectors';
import { setFullStockReportData, setFullStockReportFilters, setReportIsVisible } from '../../../../../store/slices/reportsSlice';
import KeywordFiltersSection from '../../../../../core/components/keyword-filters-section/KeywordFiltersSection';
import Button from '../../../../../core/components/button/Button';
import { getFullStockReportExcelThunk, getFullStockReportThunk } from '../../../../../store/thunks/reports/reportsThunks';
import { useFullStockReportDefaultOrdering } from '../../../../hooks/useFullStockReportDefaultOrdering';
import { getVehicleProfilesDictionary } from '../../../../../store/thunks/vehicles/vehiclesProfilesThunk';
import Dropdown from '../../../../../core/components/dropdown/Dropdown';
import { initFullStockReportData } from '../../../reports-content/stock/utils/data';

const FullStockGlobalFilters: React.FC = () => {
  const dispatch = useAppDispatch();
  const checkboxFilters = useAppSelector(fullStockReportGlobalFiltersSelector);
  const treeFilters = useAppSelector(fullStockReportTreeSelector);
  const filters = useAppSelector(fullStockReportFiltersSelector);

  const isSelectedGlobalFilters = useMemo(() => {
    if (filters) {
      const {
        corePart, businessAreas, suitableForVehicles, subcategories,
      } = filters;
      const hasSelectedCheckboxes = [businessAreas, suitableForVehicles, subcategories].some((el) => !!el && !!el.length);
      const hasCorePartValue = !isNil(corePart);
      return hasSelectedCheckboxes || hasCorePartValue;
    }
    return false;
  }, [filters]);

  const [isExpanded, setIsExpanded] = useState(false);

  const defaultOrdering = useFullStockReportDefaultOrdering();

  useEffect(() => {
    dispatch(getVehicleProfilesDictionary());

    return () => {
      dispatch(setFullStockReportFilters(defaultOrdering));
      dispatch(setFullStockReportData(initFullStockReportData));
      dispatch(setReportIsVisible(false));
    };
    // eslint-disable-next-line
  }, []);

  const clearFilters = () => {
    dispatch(setFullStockReportFilters({
      ...defaultOrdering,
      page: 1,
      pageSize: filters.pageSize,
    }));
    dispatch(setFullStockReportData(initFullStockReportData));
    dispatch(setReportIsVisible(false));
  };

  const getFilteredResHandle = (values: number[], field: string) => {
    dispatch(setFullStockReportFilters({
      ...filters,
      page: 1,
      partCode: undefined,
      [field]: values,
    }));
    dispatch(setFullStockReportData(initFullStockReportData));
    dispatch(setReportIsVisible(false));
  };

  const treeChangeHandle = (checkedKeys: React.Key[]) => {
    const checkedSubcategories = checkedKeys.filter((el) => !el.toString().includes('category'));
    const values = checkedSubcategories.map((s) => +s);
    getFilteredResHandle(values, 'subcategories');
  };

  const setSearchValue = (value: string) => {
    dispatch(setFullStockReportFilters({
      ...filters, partName: value, page: 1, partCode: undefined,
    }));
    dispatch(setFullStockReportData(initFullStockReportData));
    dispatch(setReportIsVisible(false));
  };

  const showReportHandle = () => {
    const newFilters = { ...filters, page: 1 };
    dispatch(setFullStockReportFilters(newFilters));
    dispatch(getFullStockReportThunk({ filters: newFilters }));
    dispatch(setReportIsVisible(true));
  };

  const pressEnterHandle = () => {
    if (!filters.partName?.trim().length) {
      setSearchValue('');
    } else {
      showReportHandle();
    }
  };

  const exportToExcel = () => {
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      page, pageSize, partCode, ...rest
    } = filters;
    const { order } = defaultOrdering;
    dispatch(getFullStockReportExcelThunk({
      filters: {
        ...rest,
        order,
      },
    }));
  };

  return (
    <>
      <KeywordFiltersSection
        isSearch
        searchValue={filters.partName}
        setSearchValue={setSearchValue}
        onPressEnter={pressEnterHandle}
        placeholder="Enter a part name"
        filters={checkboxFilters}
        treeFilters={treeFilters}
        treeChangeHandle={treeChangeHandle}
        isFullWidth
        onClear={clearFilters}
        className={classNames('reportFilters__filters', { 'reportFilters__filters--expanded': isExpanded })}
        getFilteredResHandle={getFilteredResHandle}
        clearSearchValue={false}
      />
      <div className="reportFilters__actions">
        <Button
          label={`${isExpanded ? 'Hide' : 'Show'} filters`}
          onClick={() => setIsExpanded(!isExpanded)}
          className={classNames('reportFilters__expand-btn', { 'reportFilters__expand-btn--active': isSelectedGlobalFilters })}
        />
        <Button
          label="Show report"
          className="reportFilters__action-button"
          icon={<FontAwesomeIcon icon={faClipboard} />}
          onClick={showReportHandle}
        />
        <Dropdown
          className="reportFilters__action-dropdown"
          icon={<FontAwesomeIcon icon={faChevronDown} />}
          menuItems={[{
            label: 'Export to Excel',
            key: 'export-to-excel',
            icon: <FontAwesomeIcon icon={faFileExcel} />,
            onClick: exportToExcel,
          }]}
          placement="bottomRight"
          parentRender
        />
      </div>
    </>
  );
};

export default FullStockGlobalFilters;
